// src/components/analytics/AnalyticsFilters.tsx
import React, { useState, useEffect } from "react";
import {
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { BillingStatus } from "../../pages/InsurancePageLayout";
import { BillingTypeEnum } from "../../types/insuranceBills.types";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import { BillingAnalyticsQueryDto } from "../../../../../../api-client";

export interface AccountOption {
  id: string;
  name: string;
}

interface Props {
  /** current filters (for controlled component) */
  value: BillingAnalyticsQueryDto;
  /** accounts for the <Select> */
  accounts: AccountOption[];
  /** called WHENEVER the user presses “Anwenden” */
  onApply: (filters: BillingAnalyticsQueryDto) => void;
  /** called when user hits “Zurücksetzen” */
  onReset: () => void;
}

const AnalyticsFilters: React.FC<Props> = ({
  value,
  accounts,
  onApply,
  onReset,
}) => {
  // local draft so the user can change inputs before hitting “apply”
  const [draft, setDraft] = useState<BillingAnalyticsQueryDto>(value);

  // keep internal draft in sync when parent resets
  useEffect(() => setDraft(value), [value]);

  /** helper to update individual draft fields */
  const handle = (field: keyof BillingAnalyticsQueryDto) => (val: any) =>
    setDraft((prev) => ({ ...prev, [field]: val }));

  const resetDraft = () => {
    onReset(); // parent decides what “reset” means
  };

  const handleDateChange =
    (field: "startDate" | "endDate") => (d: Dayjs | null) => {
      if (!d) return handle(field)(undefined);

      const iso =
        field === "endDate"
          ? d.endOf("day").toISOString()
          : d.startOf("day").toISOString();
      // console.log("ISO Date:", iso);

      handle(field)(iso);
    };

  return (
    <TopLevelPaper
      elevation={2}
      sx={{
        p: 1,
        mb: 1,
        border: "0px",
        borderRadius: "0px 0px 12px 12px",
      }}
    >
      {/* Header row with icon */}

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
        <Grid container spacing={1}>
          <Grid
            item
            md={0.7}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                ml: 1,
                color: "#45474b",
              }}
            >
              Filter
            </Typography>
          </Grid>

          {/* Bill status */}
          <Grid item md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={draft.billStatus ?? ""}
                onChange={(e) =>
                  handle("billStatus")(
                    e.target.value === "" ? undefined : e.target.value
                  )
                }
              >
                <MenuItem value="">
                  <em>Alle</em>
                </MenuItem>
                {Object.values(BillingStatus).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Bill type */}
          <Grid item md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Rechnungs­art</InputLabel>
              <Select
                label="Rechnungs­art"
                value={draft.billType ?? ""}
                onChange={(e) =>
                  handle("billType")(
                    e.target.value === "" ? undefined : e.target.value
                  )
                }
              >
                <MenuItem value="">
                  <em>Alle</em>
                </MenuItem>
                {[
                  BillingTypeEnum.GKV,
                  BillingTypeEnum.PAPER,
                  BillingTypeEnum.ZUZAHLUNG,
                ].map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Account */}
          <Grid item md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mandant</InputLabel>
              <Select
                label="Mandant"
                value={draft.accountId ?? ""}
                onChange={(e) =>
                  handle("accountId")(
                    e.target.value === "" ? undefined : e.target.value
                  )
                }
              >
                <MenuItem value="">
                  <em>Alle Accounts</em>
                </MenuItem>
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Start date */}
          <Grid
            item
            md={1.6}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <DatePicker
              label="Startdatum"
              value={draft.startDate ? dayjs(draft.startDate) : null}
              onChange={handleDateChange("startDate")}
              maxDate={draft.endDate ? dayjs(draft.endDate) : dayjs()}
              format="DD.MM.YYYY"
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>

          {/* End date */}
          <Grid
            item
            md={1.6}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <DatePicker
              label="Enddatum"
              value={draft.endDate ? dayjs(draft.endDate) : null}
              onChange={handleDateChange("endDate")}
              minDate={draft.startDate ? dayjs(draft.startDate) : undefined}
              maxDate={dayjs()}
              format="DD.MM.YYYY"
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>

          {/* Icon buttons */}
          <Grid
            item
            md={0.5}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0,
            }}
          >
            <Tooltip title="Anwenden">
              <IconButton size="small" onClick={() => onApply(draft)}>
                <FilterAltOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zurücksetzen">
              <IconButton size="small" onClick={onReset}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </LocalizationProvider>
    </TopLevelPaper>
  );
};

export default AnalyticsFilters;
